/**
 * KANBAN API — wrapper dei REST pubblicati da `KanbansResourceImpl` (crm-rest).
 * I 5 metodi CRUD standard vengono da `createCrudApi`.
 *
 * Contratto backend AGNOSTICO rispetto all'entità:
 *   - GET    /kanbans/{groupId}                 -> Page<Kanban>
 *   - POST   /kanbans/{groupId}   KanbanInput   -> Kanban
 *   - GET    /kanban/{kanbanId}                 -> Kanban  (nome, className, pipelineId)
 *   - PATCH  /kanban/{kanbanId}   KanbanInput   -> Kanban  (rinomina, filtro)
 *   - DELETE /kanban/{kanbanId}                 -> 204
 *   - POST   /kanban/{kanbanId}/populate        -> { recruited }
 *
 * I DATI delle card non passano da qui: li carica l'adapter dell'entità via il REST del **proprio**
 * tipo (`<tipo>Api.getAll({ kanbanId })`, che risolve il filtro della board lato server), e il
 * drag-to-move aggiorna la collocazione con `<tipo>Api.update({ currentStateId })`.
 *
 * L'unica operazione che tocca i record è `populate`, e nemmeno lei li conosce: chiede al server di
 * collocare chi il filtro seleziona, e riceve indietro un conteggio.
 */

import type { Page, ServerListParams } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import { createCrudApi } from '../../store/createCrudApi';
import {
  kanbanFromDto,
  kanbanCreateToInput,
  kanbanUpdateToInput,
  type KanbanDto,
  type Kanban,
  type KanbanFilterField,
} from './kanbanMappers';
import type {
  KanbanCreatePayload,
  KanbanUpdatePayload,
} from './kanbanTypes';

const crud = createCrudApi<Kanban, KanbanDto, KanbanCreatePayload, KanbanUpdatePayload, ServerListParams>({
  collection: '/kanbans',
  single: '/kanban',
  fromDto: kanbanFromDto,
  createToInput: kanbanCreateToInput,
  updateId: (p) => p.kanbanId,
  updateToInput: kanbanUpdateToInput,
});

export const kanbanApi = {
  ...crud,

  /**
   * Catalogo dei campi filtrabili: GET /kanbans/filter-fields/{groupId}.
   *
   * Envelope Page: l'openapi dichiara `type: array` ma REST Builder serializza comunque
   * Page.of(...) come {items:[...]} — la firma vera e' Page<KanbanFilterField> nella Base
   * generata. Fa fede il codice generato, non lo schema che lo ha prodotto.
   *
   * `?? []` alla cucitura: chi chiama ci itera sopra, quindi un envelope inatteso deve
   * diventare "nessun campo" qui, non un errore di rendering tre livelli piu' in la'.
   */
  getFilterFields: (groupId: number, className: string): Promise<KanbanFilterField[]> =>
    httpClient
      .get<Page<KanbanFilterField>>(
        `/kanbans/filter-fields/${groupId}?className=${encodeURIComponent(className)}`,
      )
      .then((r) => r.data?.items ?? []),

  /**
   * Popola la bacheca dal proprio filtro: POST /kanban/{kanbanId}/populate.
   *
   * Colloca nello stato iniziale della pipeline i record che il filtro seleziona e che non
   * appartengono ad alcun funnel. Non tocca chi e' gia' collocato — ne' in questa pipeline, ne' in
   * un'altra — quindi e' **ripetibile**: la seconda chiamata con lo stesso filtro restituisce 0.
   *
   * `?? 0` alla cucitura: il conteggio finisce dentro un messaggio, e un `undefined` li' si
   * leggerebbe a video come "undefined record aggiunti" invece di "nessuno".
   */
  populate: (kanbanId: number): Promise<number> =>
    httpClient
      .post<{ recruited?: number }>(`/kanban/${kanbanId}/populate`)
      .then((r) => r.data?.recruited ?? 0),
};
